import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Images from "../utils/Images";
import axiosInstance from "../utils/Api";
import { useDispatch } from "react-redux";
import { setCredentials } from "../store/authSlice";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---- Pull-to-refresh tuning (same values used on Home) ----
const PULL_THRESHOLD = 70;
const MAX_PULL = 110;

export default function LoginPage() {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // ---- Pull-to-refresh (forces a full reload so a freshly deployed
    // build gets picked up, not just a data refetch) ----
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const pullStartYRef = useRef(null);
    const pullingRef = useRef(false);

    //redux
    const dispatch = useDispatch();


    useEffect(() => {
        const handleTouchStart = (event) => {
            if (refreshing) return;
            if (window.scrollY > 0) return; // only start when already at the top
            pullStartYRef.current = event.touches[0].clientY;
            pullingRef.current = true;
        };

        const handleTouchMove = (event) => {
            if (!pullingRef.current || pullStartYRef.current === null) return;

            const deltaY = event.touches[0].clientY - pullStartYRef.current;

            if (deltaY <= 0) {
                setPullDistance(0);
                return;
            }

            if (window.scrollY > 0) {
                pullingRef.current = false;
                setPullDistance(0);
                return;
            }

            const resisted = Math.min(MAX_PULL, deltaY * 0.5);
            setPullDistance(resisted);

            if (resisted > 4) {
                event.preventDefault();
            }
        };

        const handleTouchEnd = () => {
            if (!pullingRef.current) return;
            pullingRef.current = false;

            setPullDistance((current) => {
                if (current >= PULL_THRESHOLD) {
                    setRefreshing(true);
                    window.location.reload();
                }
                return 0;
            });

            pullStartYRef.current = null;
        };

        window.addEventListener("touchstart", handleTouchStart, { passive: true });
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd, { passive: true });

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [refreshing]);

    const handleLogin = async () => {
        setError("");

        if (!EMAIL_REGEX.test(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        if (!password) {
            setError("Please enter your password.");
            return;
        }

        try {
            setLoading(true);

            const response = await axiosInstance.post("/auth/admin/signin", {
                username: email,
                userType: "LOGISTIC-USER",
                password,
            });

            const result = response.data;

            if (!result.status) {
                throw new Error(result.message || "Login failed");
            }


            const { jwt, user } = result.data;
            const passwordExpiry = user?.passwordExpiry ?? result.data.passwordExpiry;

            // Save the session. If the password has expired it is flagged, so the
            // route guards send the user to /reset-password instead of /home
            // (without the flag, PublicRoute would bounce them straight to /home).
            dispatch(
                setCredentials({
                    token: jwt,
                    user,
                    storageLocation: user?.storageLocation ?? result.data.storageLocation,
                    passwordExpired: !!passwordExpiry,
                })
            );

            navigate(passwordExpiry ? "/reset-password" : "/home", {
                replace: true,
                state: { email },
            });
        } catch (err) {
            const message =
                err.response?.data?.error ||
                err.response?.data?.data?.message ||
                err.response?.data?.message ||
                err.message ||
                "Something went wrong. Please try again.";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            className="min-h-dvh flex items-center justify-center px-4"
            style={{
                background:
                    "radial-gradient(120% 90% at 50% -10%, #0C86B8 0%, #04698F 45%, #06213F 100%)",
                paddingTop: "max(2rem, env(safe-area-inset-top))",
                paddingBottom: "max(2rem, env(safe-area-inset-bottom))",
            }}
        >
            {/* Brand fonts — Space Grotesk for display, Inter for body, IBM Plex Mono for the manifest strip */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
                .xl-display { font-family: 'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }
                @keyframes xl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
                .xl-status-dot { animation: xl-pulse 2s ease-in-out infinite; }
            `}</style>

            {/* ---- Pull-to-refresh indicator ---- */}
            {(pullDistance > 0 || refreshing) && (
                <div
                    className="fixed top-3 left-1/2 z-[150] pointer-events-none flex justify-center"
                    style={{
                        transform: `translate(-50%, ${Math.max(pullDistance, refreshing ? PULL_THRESHOLD : 0) - 40}px)`,
                        opacity: refreshing ? 1 : Math.min(1, pullDistance / PULL_THRESHOLD),
                        transition: pullingRef.current ? "none" : "transform 0.25s ease, opacity 0.25s ease",
                    }}
                >
                    <div className="w-10 h-10 rounded-full bg-white border border-[#DCEAF1] shadow-[0_10px_24px_rgba(4,33,63,0.22)] flex items-center justify-center">
                        <svg
                            width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#00A4E3" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
                            className={refreshing ? "animate-spin" : ""}
                            style={!refreshing ? { transform: `rotate(${Math.min(180, (pullDistance / PULL_THRESHOLD) * 180)}deg)` } : undefined}
                        >
                            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                            <path d="M21 3v6h-6" />
                        </svg>
                    </div>
                </div>
            )}

            <section className="w-full max-w-[400px] flex flex-col items-center">
                {/* Logo badge — logo asset untouched, seated in a white rounded square */}
                <div className="relative z-20 w-[92px] h-[92px] rounded-[26px] bg-white shadow-[0_18px_40px_rgba(4,105,143,0.45)] flex items-center justify-center p-[8px] mb-[-46px]">
                    <img
                        src={Images.logo1}
                        alt="XP StockFlow"
                        className="w-full h-full object-contain rounded-[16px]"
                    />
                </div>

                <div className="w-full bg-white rounded-[28px] shadow-[0_30px_80px_rgba(4,33,63,0.35)] pt-[58px] px-6 pb-0 overflow-hidden">
                    <div className="text-center mb-7">
                        <h1 className="xl-display text-[24px] font-bold tracking-tight text-[#08213D]">
                            XP StockFlow
                        </h1>
                    </div>
                    <label
                        htmlFor="login-email"
                        className="xl-body block text-[13px] font-semibold text-[#3D4C5E] mb-2"
                    >
                        Email address <span className="text-[#00A4E3]">*</span>
                    </label>

                    <div className="flex items-center h-[52px] rounded-[16px] border border-[#DCEAF1] bg-[#F9FCFD] overflow-hidden transition focus-within:border-[#00A4E3] focus-within:shadow-[0_0_0_4px_rgba(0,164,227,0.12)]">
                        <div className="flex-none w-[46px] min-w-[46px] h-full flex items-center justify-center">
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#00A4E3"
                                strokeWidth="1.75"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="block shrink-0"
                            >
                                <rect x="2" y="4" width="20" height="16" rx="3" />
                                <path d="m3 7 8.29 5.5a2 2 0 0 0 2.42 0L22 7" />
                            </svg>
                        </div>

                        <input
                            id="login-email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            autoFocus
                            disabled={loading}
                            value={email}
                            placeholder="Enter your email address"
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setError("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleLogin();
                                }
                            }}
                            className="xl-body flex-1 min-w-0 h-full px-0 pr-4 text-[15px] outline-none bg-transparent disabled:opacity-60"
                        />
                    </div>

                    <label
                        htmlFor="login-password"
                        className="xl-body block text-[13px] font-semibold text-[#3D4C5E] mb-2 mt-4"
                    >
                        Password <span className="text-[#00A4E3]">*</span>
                    </label>

                    <div className="flex items-center h-[52px] rounded-[16px] border border-[#DCEAF1] bg-[#F9FCFD] overflow-hidden transition focus-within:border-[#00A4E3] focus-within:shadow-[0_0_0_4px_rgba(0,164,227,0.12)]">
                        <div className="w-[46px] h-full flex items-center justify-center shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A4E3" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="4" y="10" width="16" height="11" rx="2.5" />
                                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                            </svg>
                        </div>

                        <input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            disabled={loading}
                            value={password}
                            placeholder="Enter your password"
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setError("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleLogin();
                                }
                            }}
                            className="xl-body flex-1 min-w-0 h-full px-0 text-[15px] outline-none bg-transparent disabled:opacity-60"
                        />

                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            disabled={loading}
                            className="flex-none w-[44px] min-w-[44px] h-full flex items-center justify-center text-[#8CA0B3] hover:text-[#00A4E3] active:text-[#00A4E3] transition disabled:opacity-60"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                        >
                            {showPassword ? (
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="block shrink-0"
                                >
                                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            ) : (
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="block shrink-0"
                                >
                                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 4.22-5.94M9.9 4.24A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <path d="M1 1l22 22" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="flex items-start gap-1.5 mt-3">
                            <span className="text-red-500 text-xs leading-4">⚠</span>
                            <p className="xl-body text-[12px] text-red-500 font-medium leading-4">
                                {error}
                            </p>
                        </div>
                    )}

                    <button
                        onClick={handleLogin}
                        disabled={loading}
                        className="xl-display mt-6 w-full h-[54px] rounded-[16px] text-white font-bold uppercase text-[14px] tracking-[0.08em] shadow-[0_14px_30px_rgba(0,164,227,0.4)] active:translate-y-0 hover:-translate-y-[1px] transition disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                        style={{ background: "linear-gradient(135deg, #00A4E3 0%, #04698F 100%)" }}
                    >
                        {loading ? (
                            <>
                                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                Please wait
                            </>
                        ) : (
                            <>Login →</>
                        )}
                    </button>

                    {/* Waybill / manifest tear-off strip — the signature detail */}
                    <div className="relative mt-7 -mx-6">
                        <div className="absolute left-0 right-0 top-0 border-t-2 border-dashed border-[#DCEAF1]" />
                        <div className="absolute -top-[9px] -left-[9px] w-[18px] h-[18px] rounded-full" style={{ background: "#04698F" }} />
                        <div className="absolute -top-[9px] -right-[9px] w-[18px] h-[18px] rounded-full" style={{ background: "#04698F" }} />

                        <div className="px-6 pt-4 pb-5 flex items-center justify-center gap-2">
                            <span className="xl-status-dot w-[6px] h-[6px] rounded-full bg-[#00C2B2] shrink-0" />
                            <p className="xl-mono text-[10px] tracking-[0.14em] uppercase text-[#5B6B7C]">
                                Access · Authorized Logistics Staff Only
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 text-center text-[12px] xl-body font-medium text-white/70">
                    XP StockFlow · Logistics &amp; Management
                </div>
            </section>
        </main>
    );
}